/* ──────────────────────────────────────────────────────────────
   src/pages/reports/asignaturas/AsignaturasProfesoresPage.tsx
   Informe: asignaturas con más profesores asignados
   ────────────────────────────────────────────────────────────── */

import { useMemo, useState } from 'react';
import {
  Stack,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import { useAsignaturasProfesoresReport } from '../../../hooks/useReportJob';
import type { AsignaturaProfesoresRow } from '../../../api/reports';

export default function AsignaturasProfesoresPage() {
  const { t } = useTranslation<'translation'>();
  const { run, useStatus } = useAsignaturasProfesoresReport();

  const [jobId, setJobId] = useState<string>();
  const [search, setSearch] = useState('');

  const { data: status } = useStatus(jobId);
  const pendiente = !!status && !status.done && !status.failed;

  const rows: AsignaturaProfesoresRow[] = status?.result?.rows ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => r.asignatura.toLowerCase().includes(q))
      .sort(
        (a, b) => b.numProfesores - a.numProfesores || a.asignatura.localeCompare(b.asignatura),
      );
  }, [rows, search]);

  const handleGenerate = async () => {
    const { jobId } = await run();
    setJobId(jobId);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('asigProfesores.titulo')}</Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" onClick={handleGenerate}>
          {t('asigProfesores.acciones.generar')}
        </Button>

        {rows.length > 0 && (
          <TextField
            placeholder={t<string>('asigProfesores.acciones.buscar')}
            size="small"
            sx={{ ml: 'auto', maxWidth: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {pendiente && status && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('asigProfesores.estado.generando', { progress: status.progress })}
          </Typography>
        </Stack>
      )}

      {status?.done && (
        <Alert severity="success">{t('asigProfesores.estado.exito', { count: rows.length })}</Alert>
      )}
      {status?.failed && <Alert severity="error">{t('asigProfesores.estado.error')}</Alert>}

      {status?.done && (
        <Paper>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="right" sx={{ fontWeight: 'bold', width: 60 }}>
                    {t('asigProfesores.tabla.pos')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {t('asigProfesores.tabla.asignatura')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {t('asigProfesores.tabla.profesores')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, idx) => (
                  <TableRow key={row.asignaturaId} hover>
                    <TableCell align="right">{idx + 1}</TableCell>
                    <TableCell>{row.asignatura}</TableCell>
                    <TableCell align="right">{row.numProfesores}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Stack>
  );
}
